import { useEffect, useState } from "react";
import { gql, useApolloClient } from "@apollo/client";
import { toStandardFormat } from "~/utils/datetime";
import { toDbId } from "~/utils/graphql-helpers";

export type TransactionBatchOption = {
  /** String representation of the GlTransactionType.glTransactionId */
  id: string;
  /** Display label: "{transactionTypeName} - {effectiveDate}" */
  label: string;
};

type GlTransactionRow = {
  // Accepts either the legacy integer or the string form so the hook works
  // whether or not the finpub-data-service string migration has landed. IDs are
  // normalized to strings via String(...) below to avoid precision loss.
  glTransactionId: number | string;
  transactionTypeName: string;
  effectiveDate: string | null;
  status: string | null;
};

type GlTransactionDataResult = {
  finpubGlTransactionData?: {
    success: boolean;
    transactions: GlTransactionRow[];
  };
};

export const GET_GL_TRANSACTION_DATA = gql`
  query GetGlTransactionData($arenaId: Int!, $queryId: Int, $entityIds: [Int!]) {
    finpubGlTransactionData(arenaId: $arenaId, queryId: $queryId, entityIds: $entityIds) {
      success
      transactions {
        glTransactionId
        transactionTypeName
        effectiveDate
        status
      }
    }
  }
`;

/**
 * Fetches GL transaction data for the given entity IDs using the
 * `finpubGlTransactionData` query (same source as SelectTransactionsStep).
 *
 * The transactions list is filtered server-side by `entityIds` and drives the
 * "Transactions" multi-select dropdown in the transaction-based preview step.
 * Returns an empty list when no entities are selected or the arena/query IDs
 * are not yet available.
 */
export const usePreviewTransactionBatches = (
  entityIds: string[],
  arenaDbId: number | null,
  queryId: number | null,
): { batches: TransactionBatchOption[]; loading: boolean } => {
  const client = useApolloClient();
  const [batches, setBatches] = useState<TransactionBatchOption[]>([]);
  const [loading, setLoading] = useState(false);

  // Stable string key to avoid re-running the effect on array identity changes alone.
  const entityKey = entityIds.join(",");

  useEffect(() => {
    if (!entityKey || arenaDbId == null) {
      setBatches([]);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);

    // Convert Relay global IDs → raw integer DB IDs expected by the backend.
    const entityDbIds = entityKey
      .split(",")
      .filter(Boolean)
      .map((relayId) => Number(toDbId(relayId)));

    client
      .query<GlTransactionDataResult>({
        query: GET_GL_TRANSACTION_DATA,
        variables: {
          arenaId: arenaDbId,
          queryId: queryId ?? undefined,
          entityIds: entityDbIds,
        },
        fetchPolicy: "cache-first",
      })
      .then(({ data }) => {
        if (cancelled) return;
        const transactions = data?.finpubGlTransactionData?.transactions ?? [];
        setBatches(
          transactions.map((tx) => {
            const date = tx.effectiveDate ? toStandardFormat(tx.effectiveDate) : "";
            return {
              id: String(tx.glTransactionId),
              label: [tx.transactionTypeName, date].filter(Boolean).join(" - "),
            };
          }),
        );
        setLoading(false);
      })
      .catch(() => {
        if (!cancelled) {
          setBatches([]);
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [entityKey, arenaDbId, queryId, client]);

  return { batches, loading };
};
